'use client';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

import {
	getSubscriptionEndDateAction,
	getUserSubscriptionsAction,
} from '@/entities/subscriptions/actions';
import { PAYMENT_METHODS } from '@/features/payment/const';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/form/ui/checkbox';
import { priceFormat } from '@/shared/utils';

import { usePaymentStore } from '../../store';
import { PaymentDurationType, PaymentMethodsDataType } from '../../types';
import {
	DolyamiPaymentDetails,
	FullPaymentDetails,
	LoanPaymentDetails,
	PaymentMethodSelector,
	PromoCodeInput,
} from './ui';

interface PaymentStep2Props {
	courseName: string;
	paymentMethodsData: PaymentMethodsDataType[];
	loanPrice?: number;
	dolyamiPrice?: number;
	fullPrice?: number;
	name: string;
	loanOptions: {
		label: string;
		subLabel: string;
		value: PaymentDurationType;
	}[];
	isSubscription?: boolean;
	isPromoAllowed?: boolean;
	isLongTermSubscription?: boolean;
	days?: number;
}

export const PaymentStep2 = ({
	courseName,
	paymentMethodsData,
	loanPrice,
	dolyamiPrice,
	fullPrice,
	name,
	loanOptions,
	isSubscription = false,
	isPromoAllowed = false,
	isLongTermSubscription = false,
	days: subscriptionDays,
}: PaymentStep2Props) => {
	const {
		values: formValues,
		errors,
		isPending,
		updateValues,
	} = usePaymentStore();

	const [userSubscriptionIds, setUserSubscriptionIds] = useState<string[]>([]);
	const [daysUntilNextCharge, setDaysUntilNextCharge] = useState<number | null>(
		null,
	);

	useEffect(() => {
		const fetchUserSubscriptions = async () => {
			const subscriptions = await getUserSubscriptionsAction();
			setUserSubscriptionIds(subscriptions);
		};
		fetchUserSubscriptions();
	}, []);

	useEffect(() => {
		if (!isSubscription || subscriptionDays == null) {
			setDaysUntilNextCharge(null);
			return;
		}
		const compute = async () => {
			const endDate = await getSubscriptionEndDateAction();
			const now = dayjs();
			let nextChargeDate: dayjs.Dayjs;
			if (endDate && dayjs(endDate).isValid()) {
				const currentEnd = dayjs(endDate).isAfter(now) ? dayjs(endDate) : now;
				nextChargeDate = currentEnd
					.add(subscriptionDays, 'day')
					.subtract(2, 'day');
			} else {
				nextChargeDate = now.add(subscriptionDays - 2, 'day');
			}
			const days = Math.max(
				0,
				Math.ceil(nextChargeDate.diff(now, 'day', true)),
			);
			setDaysUntilNextCharge(days);
		};
		compute();
	}, [isSubscription, subscriptionDays]);

	const currentLoanOption = useMemo(() => {
		return loanOptions.find(
			(option) => option.value === formValues.paymentDuration,
		);
	}, [formValues.paymentDuration, loanOptions]);

	const totalPrice = useMemo(() => {
		if (
			formValues.paymentMethod === PAYMENT_METHODS.LOAN &&
			loanPrice != null
		) {
			return priceFormat({ value: loanPrice });
		}
		if (formValues.paymentMethod === PAYMENT_METHODS.FULL) {
			// Если промокод валидирован, цена = 1 рубль
			if (formValues.promoCodeValidated) {
				return priceFormat({ value: 1 });
			}
			// fullPrice может быть 0 — проверяем на null/undefined, не на falsy
			if (fullPrice != null) {
				return priceFormat({ value: fullPrice });
			}
		}
		if (
			formValues.paymentMethod === PAYMENT_METHODS.DOLYAMI &&
			dolyamiPrice != null
		) {
			return priceFormat({ value: dolyamiPrice });
		}
		return null;
	}, [
		dolyamiPrice,
		formValues.paymentMethod,
		formValues.promoCodeValidated,
		fullPrice,
		loanPrice,
	]);

	const benefitPercentForFullPayment = useMemo(() => {
		if (!loanPrice || !fullPrice) {
			return null;
		}
		return Math.floor(((loanPrice - fullPrice) / loanPrice) * 100);
	}, [fullPrice, loanPrice]);

	const benefitPercentForDolyamiPayment = useMemo(() => {
		if (!loanPrice || !dolyamiPrice) {
			return null;
		}
		return Math.floor(((loanPrice - dolyamiPrice) / loanPrice) * 100);
	}, [dolyamiPrice, loanPrice]);

	const savedPriceForFullPayment = useMemo(() => {
		if (
			formValues.paymentMethod === PAYMENT_METHODS.FULL &&
			loanPrice &&
			fullPrice
		) {
			// Если промокод валидирован
			if (formValues.promoCodeValidated) {
				return loanPrice - 1;
			}
			return loanPrice - fullPrice;
		}
		return null;
	}, [
		formValues.paymentMethod,
		formValues.promoCodeValidated,
		loanPrice,
		fullPrice,
	]);

	const showPromoCodeField =
		formValues.paymentMethod === PAYMENT_METHODS.FULL && isPromoAllowed;

	const h2Title = useMemo(() => {
		if (isSubscription) {
			const subscriptionName = name || courseName;
			return subscriptionName ? `${subscriptionName}` : 'Оплата подписки';
		}
		return `${courseName}`;
	}, [isSubscription, courseName, name]);

	return (
		<>
			<Card className="-mx-4 flex min-h-[380px] md:-mx-0">
				<div className="flex flex-1 flex-col gap-8 pb-8 md:flex-row md:pb-0">
					<PaymentMethodSelector
						courseName={courseName}
						paymentMethodsData={paymentMethodsData}
						benefitPercentForFullPayment={benefitPercentForFullPayment}
						benefitPercentForDolyamiPayment={benefitPercentForDolyamiPayment}
						isSubscription={isSubscription}
					/>

					<div className="flex w-full flex-1 flex-col border-t border-dashed border-gray-4 pt-6 md:w-1/2 md:border-l md:border-t-0 md:border-dashed md:pl-6 md:pt-0">
						<h2 className="mb-2 text-xl font-semibold tracking-[0.1px] text-gray-3">
							{h2Title}
						</h2>

						<p className="text-[32px] font-semibold">
							<>
								{!!dolyamiPrice &&
									formValues.paymentMethod === PAYMENT_METHODS.DOLYAMI &&
									`4 платежа по ${priceFormat({ value: Math.floor(dolyamiPrice / 4) })}`}
								{formValues.paymentMethod === PAYMENT_METHODS.LOAN &&
									`Всего ${totalPrice}`}
								{formValues.paymentMethod === PAYMENT_METHODS.FULL &&
									`Всего ${totalPrice}`}
							</>
						</p>

						{formValues.paymentMethod === PAYMENT_METHODS.FULL && (
							<FullPaymentDetails
								userSubscriptionIds={userSubscriptionIds}
								fullPrice={fullPrice}
								isSubscription={isSubscription}
								isLongTermSubscription={isLongTermSubscription}
								savedPriceForFullPayment={savedPriceForFullPayment}
								daysUntilNextCharge={daysUntilNextCharge}
							/>
						)}

						{!!dolyamiPrice &&
							formValues.paymentMethod === PAYMENT_METHODS.DOLYAMI && (
								<DolyamiPaymentDetails totalPrice={totalPrice} />
							)}

						{formValues.paymentMethod === PAYMENT_METHODS.LOAN && (
							<LoanPaymentDetails
								currentLoanOption={currentLoanOption}
								loanOptions={loanOptions}
							/>
						)}

						{showPromoCodeField && <PromoCodeInput />}

						<div className="mt-auto pt-6">
							<div className="mb-4 text-gray-3">
								<Checkbox
									onChange={(e) => updateValues({ isAgree: e.target.checked })}
									checked={formValues.isAgree}
									invalid={Boolean(errors?.isAgree)}
									label={
										<div className="text-gray-3">
											Подтверждаю, что ознакомлен с{' '}
											<Link
												href="/docs/offerta"
												target="_blank"
												className="text-accent hover:underline"
											>
												Офертой
											</Link>
										</div>
									}
								/>
								{errors?.isAgree && (
									<p className="mt-1 text-sm text-red-500">
										{errors.isAgree.join(', ')}
									</p>
								)}
							</div>
							<Button block type="submit" className="h-12" loading={isPending}>
								Продолжить
							</Button>
						</div>
					</div>
				</div>
			</Card>
		</>
	);
};
